'use client';

/**
 * /p/[slug] layout — PERSISTENT project chrome. Header + tab bar stay put
 * while the actual tool pages render inside; navigation never dead-ends
 * (Boss, 2026-07-07: "if I click funnels there's no way back — fix").
 */
import { use } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { notFound } from 'next/navigation';
import {
  Factory, Plane, Flower2, Target, MoonStar, Bot, ExternalLink,
  LayoutDashboard, Globe, Gauge, Wrench, Hammer, Filter, Video,
  FlaskConical, Megaphone, FileText, Users, Mail, Briefcase, Plus,
} from 'lucide-react';
import { getBusinessProject, BUSINESS_PROJECTS, isPlaceholderCollab, type ProjectTab } from '@/lib/business-projects';

export const PROJECT_ICONS: Record<string, any> = {
  Factory, Plane, Flower2, Target, MoonStar, Bot, Users,
};

const TAB_DEFS: Record<ProjectTab, { label: string; icon: any }> = {
  websites: { label: 'Websites', icon: Globe },
  performance: { label: 'Performance', icon: Gauge },
  improve: { label: 'Improve', icon: Wrench },
  factory: { label: 'Factory', icon: Hammer },
  funnel: { label: 'Funnel', icon: Filter },
  'video-lab': { label: 'Video Lab', icon: Video },
  'ab-tests': { label: 'A/B Tests', icon: FlaskConical },
  ads: { label: 'Ads', icon: Megaphone },
  content: { label: 'Content', icon: FileText },
  crm: { label: 'CRM', icon: Users },
  email: { label: 'Email', icon: Mail },
  investors: { label: 'Investors', icon: Briefcase },
};

export default function ProjectLayout({ children, params }: {
  children: React.ReactNode;
  params: Promise<{ slug: string }>;
}) {
  const { slug } = use(params);
  const pathname = usePathname();
  const project = getBusinessProject(slug);
  if (!project) return notFound();

  const Icon = PROJECT_ICONS[project.icon] || Factory;
  const base = `/p/${slug}`;
  const tabHref = (t: ProjectTab) => {
    let q = '';
    if (project.funnelId && ['funnel', 'video-lab'].includes(t)) q = `?funnel=${project.funnelId}`;
    else if (t === 'ads' && project.adsAccountId) q = `?account=${project.adsAccountId}`;
    else if (t === 'performance') {
      // scope GSC performance to THIS project's domains
      if (project.sites !== 'factory') q = `?sites=${project.sites.map(s => s.domain).join(',')}`;
      else {
        const others = BUSINESS_PROJECTS.flatMap(p => (p.slug !== project.slug && p.sites !== 'factory' ? p.sites.map(s => s.domain) : []));
        if (others.length) q = `?exclude=${others.join(',')}`;
      }
    }
    return `${base}/${t}${q}`;
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Icon className={`w-6 h-6 ${project.color}`} />
              <div>
                <h1 className="text-xl font-semibold text-white">{project.name}</h1>
                <p className="text-xs text-gray-500">{project.tagline}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isPlaceholderCollab(slug) && (
                <Link href={`/projects?create=1&collab=${slug}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-xs text-cyan-300 hover:bg-cyan-500/25">
                  <Plus className="w-3 h-3" /> New Project
                </Link>
              )}
              {(project.links || []).map(l => (
                <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-xs text-gray-300 hover:text-white">
                  {l.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
              {project.funnelUrl && (
                <a href={project.funnelUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-xs text-gray-300 hover:text-white">
                  Live funnel <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* tab bar removed — the sidebar carries per-project tabs (Boss, 2026-07-04:
          "performance/improve are in the sidebar, I don't need them at the top") */}
      {children}
    </div>
  );
}
